import { Link } from "wouter";
import { useCallback, useRef } from "react";
import { queryClient } from "@/lib/queryClient";

const chunkLoaders: Record<string, () => Promise<any>> = {
  "/about": () => import("@/pages/About"),
  "/events": () => import("@/pages/Events"),
  "/flash": () => import("@/pages/FlashDesigns"),
  "/shop": () => import("@/pages/Shop"),
  "/contact": () => import("@/pages/Contact"),
  "/healing": () => import("@/pages/HealingTracker"),
  "/aftercare": () => import("@/pages/Aftercare"),
  "/quiz": () => import("@/pages/StyleQuiz"),
  "/booking": () => import("@/pages/BookingCalendar"),
  "/estimate": () => import("@/pages/CostEstimator"),
  "/privacy": () => import("@/pages/Privacy"),
  "/terms": () => import("@/pages/Terms"),
  "/accessibility": () => import("@/pages/Accessibility"),
};

const dataQueries: Record<string, string[]> = {
  "/events": ["/api/events"],
  "/booking": ["/api/events/upcoming"],
};

const prefetched = new Set<string>();

function prefetchRoute(href: string) {
  const basePath = href.startsWith("/artist/") ? "/artist" : href.split("?")[0].split("#")[0];

  if (prefetched.has(basePath)) return;
  prefetched.add(basePath);

  const loader = chunkLoaders[basePath];
  if (loader) {
    loader();
  }

  if (basePath.startsWith("/artist")) {
    import("@/pages/Artist");
  }

  const queries = dataQueries[basePath];
  if (queries) {
    queries.forEach((key) => {
      queryClient.prefetchQuery({
        queryKey: [key],
        staleTime: 60_000,
      });
    });
  }
}

interface PrefetchLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  children: React.ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function PrefetchLink({ href, children, className, onMouseEnter, onFocus, ...props }: PrefetchLinkProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleHoverStart = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      timerRef.current = setTimeout(() => prefetchRoute(href), 65);
      if (onMouseEnter) onMouseEnter(e);
    },
    [href, onMouseEnter]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLAnchorElement>) => {
      prefetchRoute(href);
      if (onFocus) onFocus(e);
    },
    [href, onFocus]
  );

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  return (
    <Link
      href={href}
      className={className}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      {...props}
    >
      {children}
    </Link>
  );
}
